import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Calendar, CreditCard } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage({ token }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight" data-testid="history-title">
          {t('history')}
        </h1>
        <p className="text-lg text-slate-600">{t('recent_payments')}</p>
      </div>

      {/* Payment History List */}
      {history.length > 0 ? (
        <div className="space-y-4" data-testid="payment-history-list">
          {history.map((payment) => (
            <Card
              key={payment.id}
              className="p-6 bg-white rounded-2xl border border-slate-200 hover:shadow-lg transition-all duration-300"
              data-testid="payment-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-xl">
                    <Receipt className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{payment.provider}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline">{t(payment.category)}</Badge>
                      <span className="text-sm text-slate-600">
                        <Calendar className="inline h-4 w-4 mr-1" />
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">₪{payment.amount.toFixed(2)}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    <CreditCard className="inline h-4 w-4 mr-1" />
                    {payment.payment_method}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 bg-white rounded-2xl border border-slate-200 text-center" data-testid="no-payments">
          <Receipt className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <p className="text-xl text-slate-600">{t('no_payments')}</p>
        </Card>
      )}
    </div>
  );
}